import APIRequest from "./APIRequest";

/**
 * Fetch list of all active ad categories
 * Route: GET /list-ad-categories
 */
export const getAdCategories = () => {
  return APIRequest.get("list-ad-categories");
};

/**
 * Fetch ad clips belonging to a specific category
 * Route: GET /adclips-listing?ad_category_id={id}
 */
export const getAdClips = (categoryId) => {
  return APIRequest.get("adclips-listing", { ad_category_id: categoryId });
};

// Internal tracking of last served category and clip to ensure continuous variety
let lastServedCategoryId = null;
let lastServedClipId = null;

export const resetAdHistory = () => {
  lastServedCategoryId = null;
  lastServedClipId = null;
};

/**
 * Intelligently retrieves a randomized ad clip:
 * 1. Randomly selects an ad category (avoiding immediate back-to-back repetitions if > 1 category exists).
 * 2. Fetches clips for the selected category.
 * 3. Randomly selects a clip (avoiding immediate back-to-back repetitions if > 1 clip exists).
 * 4. Gracefully falls back to another category if the chosen category contains 0 clips.
 * 5. Returns { clip, category } or null if no ads are available.
 */
export const getRandomAdClip = async () => {
  try {
    // 1. Fetch available categories
    const categoriesResponse = await getAdCategories();
    const categories = categoriesResponse?.data?.data || [];

    if (!categoriesResponse.ok || !categories.length) {
      return null;
    }

    // 2. Select category: avoid repeating last served category if possible
    let eligibleCategories =
      categories.length > 1 && lastServedCategoryId !== null
        ? categories.filter((c) => c.id !== lastServedCategoryId)
        : categories;

    if (!eligibleCategories.length) {
      eligibleCategories = categories;
    }

    // Pick random category from eligible list
    const chosenCategory =
      eligibleCategories[Math.floor(Math.random() * eligibleCategories.length)];
    lastServedCategoryId = chosenCategory.id;

    // 3. Fetch clips for the chosen category
    let clipsResponse = await getAdClips(chosenCategory.id);
    let clips = clipsResponse?.data?.data || [];

    // Fallback: If chosen category has 0 clips, search other categories
    let finalCategory = chosenCategory;
    if (!clips.length && categories.length > 1) {
      const remainingCategories = categories.filter((c) => c.id !== chosenCategory.id);
      for (const cat of remainingCategories) {
        const fallbackResponse = await getAdClips(cat.id);
        const fallbackClips = fallbackResponse?.data?.data || [];
        if (fallbackClips.length > 0) {
          clips = fallbackClips;
          finalCategory = cat;
          lastServedCategoryId = cat.id;
          break;
        }
      }
    }

    if (!clips.length) {
      return null;
    }

    // 4. Select clip: avoid repeating last served clip if multiple are available
    let eligibleClips =
      clips.length > 1 && lastServedClipId !== null
        ? clips.filter((c) => c.id !== lastServedClipId)
        : clips;

    if (!eligibleClips.length) {
      eligibleClips = clips;
    }

    const chosenClip =
      eligibleClips[Math.floor(Math.random() * eligibleClips.length)];
    lastServedClipId = chosenClip.id;

    return {
      clip: chosenClip,
      category: finalCategory,
    };
  } catch (error) {
    console.error("Error fetching random ad clip:", error);
    return null;
  }
};

const adService = {
  getAdCategories,
  getAdClips,
  getRandomAdClip,
  resetAdHistory,
};

export default adService;
